import { useState, useEffect, useCallback, useRef } from "react";
import { isActiveEmp } from "../lib/constants";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  Banknote, ReceiptText, TrendingUp, Target, Plus, Trash2, Loader2, Upload, Store, Users, CalendarDays,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { BulkTools } from "../components/BulkTools";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";

const money = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 }));
const monthNow = () => new Date().toISOString().slice(0, 7);

const KPI = ({ icon: Icon, label, value, sub, tone = "blue" }) => {
  const tones = { blue: "text-blue-600 bg-blue-50", green: "text-emerald-600 bg-emerald-50", amber: "text-amber-600 bg-amber-50", violet: "text-violet-600 bg-violet-50" };
  return (
    <div data-testid={`sales-kpi-${label}`} className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-3">
        <span className={`grid h-10 w-10 place-items-center rounded-xl ${tones[tone]}`}><Icon className="h-5 w-5" /></span>
        <div className="min-w-0">
          <p className="text-xs text-slate-500 leading-tight">{label}</p>
          <p className="font-heading text-xl font-bold text-slate-800 tabular-nums">{value}</p>
        </div>
      </div>
      {sub && <p className="mt-2 text-xs text-slate-400">{sub}</p>}
    </div>
  );
};

export const SalesPanel = ({ canEdit = false, branches = [], employees = [] }) => {
  const branchNames = branches.map((b) => (typeof b === "string" ? b : b.branch)).filter(Boolean);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(monthNow() + "-01");
  const [to, setTo] = useState("");
  const [branch, setBranch] = useState("");
  const [target, setTarget] = useState(0);
  const [targetInput, setTargetInput] = useState("");
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const [form, setForm] = useState({ date: monthNow() + "-01", branch: "", employee_id: "", amount: "", invoices: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (branch) params.branch = branch;
      const [res, tg] = await Promise.all([api.listSales(params), api.getSalesTargets(monthNow())]);
      setData(res);
      const t = (tg.targets || []).find((x) => !x.employee_id && !x.branch);
      setTarget(t ? t.target_amount : 0);
    } catch {
      toast.error("تعذّر تحميل بيانات المبيعات");
    } finally {
      setLoading(false);
    }
  }, [from, to, branch]);

  useEffect(() => { load(); }, [load]);

  const addEntry = async (e) => {
    e.preventDefault();
    if (!form.date || !form.amount) { toast.error("التاريخ والمبلغ مطلوبان"); return; }
    setSaving(true);
    try {
      await api.createSales({
        date: form.date, branch: form.branch, employee_id: form.employee_id || null,
        amount: Number(form.amount), invoices: Number(form.invoices || 0), notes: form.notes,
      });
      setForm((f) => ({ ...f, amount: "", invoices: "", notes: "" }));
      toast.success("تم تسجيل المبيعات");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذّر الحفظ");
    } finally { setSaving(false); }
  };

  const removeEntry = async (id) => {
    try { await api.deleteSales(id); toast.success("تم الحذف"); load(); }
    catch { toast.error("تعذّر الحذف"); }
  };

  const saveTarget = async () => {
    if (!targetInput) return;
    try {
      await api.setSalesTarget({ month: monthNow(), target_amount: Number(targetInput) });
      setTarget(Number(targetInput)); setTargetInput("");
      toast.success("تم حفظ هدف الشهر");
    } catch { toast.error("تعذّر حفظ الهدف"); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const res = await api.importSales(file);
      toast.success(`تم استيراد ${res.inserted} سجل${res.updated ? ` · حُدّث ${res.updated}` : ""}${res.skipped ? ` · تُخطّي ${res.skipped} مكرر` : ""}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذّر الاستيراد");
    } finally { setImporting(false); }
  };

  if (loading) return <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  const s = data?.summary || {};
  const monthTotal = (data?.trend || []).filter((t) => t.date.startsWith(monthNow())).reduce((a, b) => a + b.amount, 0);
  const achievement = target > 0 ? Math.round((monthTotal / target) * 100) : null;

  return (
    <div className="space-y-5" data-testid="sales-panel">
      {/* Filters + actions */}
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="text-xs text-slate-500">من</label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" data-testid="sales-from" />
        </div>
        <div>
          <label className="text-xs text-slate-500">إلى</label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" data-testid="sales-to" />
        </div>
        <div>
          <label className="text-xs text-slate-500 block">الفرع</label>
          <select value={branch} onChange={(e) => setBranch(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="sales-branch-filter">
            <option value="">كل الفروع</option>
            {branchNames.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        {canEdit && (
          <>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden onChange={onImport} />
            <BulkTools section="sales" onDone={load} />
            <Button variant="outline" className="h-9 gap-1" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="sales-import-btn">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} استيراد Excel
            </Button>
          </>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI icon={Banknote} label="إجمالي المبيعات" value={money(s.total_amount)} tone="green" sub={`${s.count || 0} عملية`} />
        <KPI icon={ReceiptText} label="عدد الفواتير" value={money(s.total_invoices)} tone="blue" />
        <KPI icon={CalendarDays} label="متوسط اليوم" value={money(s.avg_per_day)} tone="violet" sub={`${s.days || 0} يوم`} />
        <KPI icon={Target} label={`تحقيق هدف ${monthNow()}`} value={achievement === null ? "—" : `${achievement}%`} tone="amber"
          sub={target > 0 ? `${money(monthTotal)} / ${money(target)}` : "لم يُحدد هدف"} />
      </div>

      {/* Target setter */}
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50/60 p-3">
          <Target className="h-4 w-4 text-amber-600" />
          <span className="text-sm text-slate-600">هدف مبيعات شهر {monthNow()}:</span>
          <Input type="number" placeholder="المبلغ" value={targetInput} onChange={(e) => setTargetInput(e.target.value)} className="h-9 w-40" data-testid="sales-target-input" />
          <Button className="h-9 bg-amber-600 hover:bg-amber-700" onClick={saveTarget} data-testid="sales-target-save">حفظ الهدف</Button>
          {target > 0 && <span className="text-xs text-slate-400">الحالي: {money(target)}</span>}
        </div>
      )}

      {/* Add entry */}
      {canEdit && (
        <form onSubmit={addEntry} className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="sales-add-form">
          <p className="font-heading font-bold text-slate-700 text-sm flex items-center gap-1.5"><Plus className="h-4 w-4" /> تسجيل مبيعات</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="sales-date" />
            <select value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="sales-branch">
              <option value="">الفرع (اختياري)</option>
              {branchNames.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="sales-employee">
              <option value="">الموظف (اختياري)</option>
              {employees.filter(isActiveEmp).map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
            </select>
            <Input type="number" step="0.01" placeholder="مبلغ المبيعات" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="sales-amount" />
            <Input type="number" placeholder="عدد الفواتير" value={form.invoices} onChange={(e) => setForm({ ...form, invoices: e.target.value })} data-testid="sales-invoices" />
            <Input placeholder="ملاحظات" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="sales-notes" />
          </div>
          <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-1" data-testid="sales-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} حفظ
          </Button>
        </form>
      )}

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="font-heading font-bold text-sm text-slate-700 mb-2 flex items-center gap-1.5"><TrendingUp className="h-4 w-4 text-blue-600" /> اتجاه المبيعات اليومي</p>
          <div className="h-56" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.trend || []} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} width={48} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Line type="monotone" dataKey="amount" name="المبيعات" stroke="#2563eb" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="font-heading font-bold text-sm text-slate-700 mb-2 flex items-center gap-1.5"><Store className="h-4 w-4 text-emerald-600" /> المبيعات حسب الفرع</p>
          <div className="h-56" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.by_branch || []} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="branch" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} width={48} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Bar dataKey="amount" name="المبيعات" fill="#10b981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top employees */}
      {(data?.top_employees || []).length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="font-heading font-bold text-sm text-slate-700 mb-3 flex items-center gap-1.5"><Users className="h-4 w-4 text-violet-600" /> أعلى الموظفين مبيعاً</p>
          <div className="space-y-1.5">
            {data.top_employees.map((e, i) => (
              <div key={e.employee_id} className="flex items-center gap-2 text-sm" data-testid={`sales-top-emp-${e.employee_id}`}>
                <span className="h-6 w-6 shrink-0 rounded-full bg-slate-100 text-slate-500 text-xs flex items-center justify-center font-bold">{i + 1}</span>
                <span className="text-slate-700 truncate">{e.name}</span>
                <span className="mr-auto font-bold tabular-nums text-emerald-600">{money(e.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Entries table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-right px-4 py-2.5 font-medium">التاريخ</th>
                <th className="text-right px-4 py-2.5 font-medium">الفرع</th>
                <th className="text-right px-4 py-2.5 font-medium">الموظف</th>
                <th className="text-right px-4 py-2.5 font-medium">المبلغ</th>
                <th className="text-right px-4 py-2.5 font-medium">الفواتير</th>
                {canEdit && <th className="px-4 py-2.5"></th>}
              </tr>
            </thead>
            <tbody>
              {(data?.entries || []).map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`sales-row-${r.id}`}>
                  <td className="px-4 py-2.5 tabular-nums">{r.date}</td>
                  <td className="px-4 py-2.5">{r.branch || "—"}</td>
                  <td className="px-4 py-2.5">{r.employee_name || "—"}</td>
                  <td className="px-4 py-2.5 font-semibold tabular-nums text-emerald-700">{money(r.amount)}</td>
                  <td className="px-4 py-2.5 tabular-nums">{r.invoices || 0}</td>
                  {canEdit && (
                    <td className="px-4 py-2.5 text-left">
                      <button onClick={() => removeEntry(r.id)} className="text-slate-400 hover:text-rose-600" data-testid={`sales-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                    </td>
                  )}
                </tr>
              ))}
              {(data?.entries || []).length === 0 && (
                <tr><td colSpan={canEdit ? 6 : 5} className="px-4 py-10 text-center text-slate-400">لا توجد بيانات مبيعات في هذه الفترة</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
